import { useState, useEffect, useRef, useCallback } from "react";
import { Volume2, VolumeX, ChevronRight, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import API from "@/lib/api";
import { analytics } from "@/instrument";

/**
 * First-visit walkthrough for new patients.
 *
 * - 4 steps highlighting Account Number, Order Status meanings, Receipts,
 *   and Subscriptions.
 * - Each step has a target element selector (data-testid) that gets a soft
 *   spotlight ring; if the element doesn't exist on the page (e.g. the
 *   patient has no orders yet) the step still renders, just without the
 *   spotlight — the copy still teaches.
 * - Audio narration uses the browser's built-in Web Speech API; we pick a
 *   British female voice when available (Google UK English Female / en-GB
 *   Female / Sonia / Libby / Karen) and fall back gracefully on systems
 *   without one.
 * - "Don't show again" is persisted in localStorage so it doesn't reappear
 *   on the same browser. Tour is also dismissed via X or Skip.
 */

const STORAGE_KEY = "glpflo_patient_tour_v1";
const TOUR_VERSION = "v1";

/**
 * Dual-channel event recorder.  Sends each tour event to:
 *  - PostHog (consent-gated, client-side aggregation) via analytics.capture
 *  - Backend (consent-independent, server-side tally for the admin tile)
 *    via POST /api/patient/tour-event
 * Both calls are best-effort and never block the UI.
 */
function recordTourEvent(event, extra = {}) {
  try { analytics?.capture?.(`patient_tour_${event}`, { tour_version: TOUR_VERSION, ...extra }); } catch { /* noop */ }
  try {
    API.post("/patient/tour-event", { event, tour_version: TOUR_VERSION, ...extra }).catch(() => {});
  } catch { /* noop */ }
}

const STEPS = [
  {
    id: "account-number",
    targetSelector: '[data-testid="patient-account-chip"]',
    title: "Your account number",
    say: "Welcome to GLPFlo! This green chip at the top of your dashboard is your unique account number. Share it with our support team whenever you reach out — it helps us find your records in seconds. You'll also see it on your receipts and confirmation emails.",
    body: "The emerald chip in your dashboard header is your unique GLPFlo account number. Share it with support to speed up any conversation — they'll find your records instantly.",
  },
  {
    id: "order-status",
    targetSelector: '[data-testid="pending-orders"]',
    title: "What each order status means",
    say: "Your orders move through three steps. Pending Review means a clinician is checking your medication is right for you. Pending Payment means you've been approved and just need to complete checkout. Approved means we've sent your prescription to our pharmacy partner for fulfillment.",
    body: "Pending Review → clinician verifies. Pending Payment → tap to pay. Approved → headed to pharmacy. You'll get an email at every step.",
  },
  {
    id: "receipts",
    targetSelector: '[data-testid="patient-help-receipts"]',
    title: "Finding your receipts",
    say: "Need a receipt for HSA, FSA, or your records? Every completed order has a Receipt button. Tap it to view or download a PDF showing the date, your medication and dose, and the amount charged. We also email a copy automatically.",
    body: "Every completed order has a Receipt button — view, download, or share for HSA, FSA, or your own records. A copy is also emailed automatically.",
  },
  {
    id: "subscriptions",
    targetSelector: '[data-testid="patient-help-subscriptions"]',
    title: "Managing your subscription",
    say: "If you're on a recurring plan, your Subscriptions card lets you pause for thirty days if you're traveling, cancel future renewals, or switch tiers between Monthly and Royal. All changes apply at your next renewal — you never lose paid time.",
    body: "Pause, cancel, or change tier anytime — all from your Subscriptions card. Changes apply at your next renewal so you never lose access early.",
  },
];

/**
 * Browser-side TTS using SpeechSynthesis. Returns a `say` function and a
 * `cancel` function. Voice selection waits for `voiceschanged` because
 * `getVoices()` returns empty on first call in many browsers.
 */
function useBritishVoice() {
  const voiceRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return undefined;
    const pickVoice = () => {
      const all = window.speechSynthesis.getVoices() || [];
      if (all.length === 0) return;
      // Preference order — most-British-female-sounding first.
      const preferred = [
        (v) => /Google UK English Female/i.test(v.name),
        (v) => /en[-_]GB/i.test(v.lang) && /female|sonia|libby|hazel|karen|amy|emma/i.test(v.name),
        (v) => /en[-_]GB/i.test(v.lang),
        (v) => /British/i.test(v.name),
      ];
      let chosen = null;
      for (const matcher of preferred) {
        chosen = all.find(matcher);
        if (chosen) break;
      }
      voiceRef.current = chosen || all.find((v) => /en/i.test(v.lang)) || all[0] || null;
      setReady(true);
    };
    pickVoice();
    window.speechSynthesis.addEventListener("voiceschanged", pickVoice);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", pickVoice);
  }, []);

  const say = useCallback((text) => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      if (voiceRef.current) u.voice = voiceRef.current;
      u.lang = voiceRef.current?.lang || "en-GB";
      u.rate = 1.02;
      u.pitch = 1.05;
      u.volume = 0.95;
      window.speechSynthesis.speak(u);
    } catch {
      /* speechSynthesis may be unavailable; tour still works silently */
    }
  }, []);

  const cancel = useCallback(() => {
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
  }, []);

  return { say, cancel, ready, voiceName: voiceRef.current?.name || "" };
}

export default function PatientHelpTour({ forceOpen = false, onClose }) {
  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [audioOn, setAudioOn] = useState(true);
  const { say, cancel, voiceName } = useBritishVoice();
  const spotlightRef = useRef(null);

  // First-visit auto-trigger. localStorage gate so it never repeats.
  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      recordTourEvent("replayed");
      return;
    }
    try {
      if (window.localStorage.getItem(STORAGE_KEY)) return;
    } catch { return; }
    // Tiny delay so the dashboard finishes painting + cookie-banner closes.
    const t = setTimeout(() => {
      setOpen(true);
      recordTourEvent("started");
    }, 1800);
    return () => clearTimeout(t);
  }, [forceOpen]);

  // Spotlight: add a temporary ring to the target element and scroll to it.
  useEffect(() => {
    if (!open) return undefined;
    const step = STEPS[stepIdx];
    const el = step?.targetSelector ? document.querySelector(step.targetSelector) : null;
    if (el) {
      try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* noop */ }
      el.classList.add("ring-4", "ring-emerald-300", "ring-offset-2", "ring-offset-white", "rounded-full");
      spotlightRef.current = el;
    }
    // Narrate.
    if (audioOn && step?.say) say(step.say);
    return () => {
      if (spotlightRef.current) {
        spotlightRef.current.classList.remove("ring-4", "ring-emerald-300", "ring-offset-2", "ring-offset-white", "rounded-full");
        spotlightRef.current = null;
      }
    };
  }, [open, stepIdx, audioOn, say]);

  const close = useCallback((markSeen = true, alreadyLogged = false) => {
    cancel();
    setOpen(false);
    if (markSeen) {
      try {
        window.localStorage.setItem(STORAGE_KEY, new Date().toISOString());
        window.dispatchEvent(new CustomEvent("glpflo:tour-seen"));
      } catch { /* noop */ }
      // If the user reached the end and we already fired `completed`, skip
      // the duplicate `skipped` event. Otherwise this was an X/backdrop/Escape
      // dismissal — record it as a skip with the step they bailed on.
      if (!alreadyLogged) {
        recordTourEvent("skipped", { step_idx: stepIdx });
      }
    }
    if (onClose) onClose();
  }, [cancel, onClose, stepIdx]);

  const advance = () => {
    // Record which step the user is leaving (helps spot drop-off points).
    recordTourEvent("step_advance", { step_idx: stepIdx });
    if (stepIdx >= STEPS.length - 1) {
      recordTourEvent("completed", { step_idx: stepIdx });
      close(true, /* alreadyLogged */ true);
    } else {
      setStepIdx((i) => i + 1);
    }
  };

  const toggleAudio = () => {
    setAudioOn((v) => {
      const next = !v;
      if (!next) cancel();
      else if (STEPS[stepIdx]?.say) say(STEPS[stepIdx].say);
      return next;
    });
  };

  if (!open) return null;
  const step = STEPS[stepIdx];

  return (
    <>
      {/* Dim backdrop. The dashboard remains visible underneath so the patient
          can still see the surface being explained, but actions are blocked
          until they tap a tour button. */}
      <div
        className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[2px] animate-in fade-in duration-200"
        onClick={() => close(true)}
        data-testid="patient-tour-backdrop"
      />
      <div
        role="dialog"
        aria-label={step.title}
        className="fixed z-50 left-1/2 -translate-x-1/2 bottom-6 sm:bottom-10 w-[92vw] sm:w-[480px] rounded-2xl bg-white shadow-2xl border-2 border-emerald-200 overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
        data-testid="patient-tour-card"
      >
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2.5 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Quick welcome tour</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={toggleAudio}
              className="p-1 rounded hover:bg-white/20 transition"
              aria-label={audioOn ? "Mute narration" : "Unmute narration"}
              title={audioOn ? `Mute (voice: ${voiceName || "default"})` : "Unmute narration"}
              data-testid="patient-tour-audio-toggle"
            >
              {audioOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={() => close(true)}
              className="p-1 rounded hover:bg-white/20 transition"
              aria-label="Close tour"
              data-testid="patient-tour-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="px-5 py-4">
          <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-1" data-testid="patient-tour-step-indicator">
            Step {stepIdx + 1} of {STEPS.length}
          </p>
          <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="patient-tour-step-title">
            {step.title}
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed" data-testid="patient-tour-step-body">
            {step.body}
          </p>
        </div>

        <div className="px-5 pb-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => close(true)}
            className="text-xs text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline transition"
            data-testid="patient-tour-skip"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {/* Step dots */}
            <div className="flex gap-1 mr-1">
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${i === stepIdx ? "w-5 bg-emerald-500" : "w-1.5 bg-slate-300"}`}
                />
              ))}
            </div>
            <Button
              onClick={advance}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 h-9"
              data-testid="patient-tour-next"
            >
              {stepIdx === STEPS.length - 1 ? "Done" : "Next"}
              {stepIdx < STEPS.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
